import styled, { css } from 'styled-components';

export const Input = styled.input.withConfig({
  shouldForwardProp: (prop) => !['readonly'].includes(prop),
})`
  background-color: ${(props) => props.theme.colors.transparent};
  border: none;
  border-radius: 4px;
  height: 100%;
  //to fix focus ring width/positioning
  margin-inline: -10px;
  padding-inline: 10px;
  width: calc(100% + 10px * 2);

  @media ${(props) => props.theme.device.mobileL} {
    //to fix focus ring width/positioning
    margin-inline: -5px;
    padding-inline: 10px;
    width: calc(100% + 5px);
  }

  font-family: ${({ theme }) => theme.fontset.titillium};
  font-size: 14px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.29;
  letter-spacing: normal;
  text-overflow: ellipsis;
  color: ${(props) =>
    props.type === 'date'
      ? props.theme.colors.textSecondary
      : props.theme.colors.white};
  position: ${(props) =>
    props.type === 'date' && props.label ? 'absolute' : 'none'};
  top: ${(props) => (props.type === 'date' && props.label ? '0' : 'none')};
  right: ${(props) => (props.type === 'date' && props.label ? '10px' : 'none')};
  max-width: ${(props) =>
    props.type === 'date' && props.label ? '160px' : 'none'};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.focusRing};
    outline-offset: -1px;
  }

  &::placeholder {
    font-weight: 600;
    font-style: normal;
    color: ${(props) => props.theme.colors.textSecondary};
    opacity: 50%;
  }

  &::-webkit-input-placeholder {
    /* Edge */
    font-weight: 600;
    color: ${(props) => props.theme.colors.textSecondary};
    opacity: 50%;
  }

  &:-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    font-weight: 600;
    color: ${(props) => props.theme.colors.textSecondary};
    opacity: 50%;
  }

  &[type='number']::-webkit-inner-spin-button,
  &[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
    `}

  ${({ readonly }) =>
    readonly &&
    css`
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `}
`;

const converter = {
  s: '200px',
  m: '315px',
  l: '430px',
  full: '100%',
};

export const InputContainer = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !['hasIcon', 'valid', 'readonly', 'error', 'focus'].includes(prop),
})`
  position: relative;
  min-width: 65px;
  height: 40px;
  background-color: ${(props) => props.theme.colors.white};
  padding: 0 10px;
  border: 1px solid ${(props) => props.theme.colors.link};
  border-radius: 4px;

  ${({ size }) =>
    Number.isInteger(size)
      ? css`
          width: ${size}px;
          max-width: ${size}px;
        `
      : Object.keys(converter).includes(size)
      ? css`
          width: ${converter[size]};
          max-width: ${converter[size]};
        `
      : null}

  & input {
    color: ${(props) => props.theme.colors.textPrimary};
  }

  & ::placeholder {
    opacity: 1;
  }

  ${(props) =>
    props.hasIcon &&
    css`
      input {
        width: calc(100% - 40px);
        margin-left: 10px;
      }
      svg {
        fill: none;
        stroke: ${props.theme.colors.themeColor};
      }
    `};

  // custom classes
  &.search {
    border: none;
    border-radius: 9px;
    background: ${(props) => props.theme.colors.white};
    box-shadow: 0px 4px 20px 0px rgba(104, 104, 104, 0.1);
    min-width: 290px;

    & input {
      //to fix focus ring width/positioning
      margin-right: -20px;
      width: calc(100% - 20px);

      @media ${(props) => props.theme.device.mobileL} {
        width: calc(100% - 30px);
      }

      // hide default clear button of <input type="search" /> in favor of the custom one
      &::-webkit-search-cancel-button {
        display: none;
      }
    }

    @media ${(props) => props.theme.device.mobileL} {
      min-width: 207px;
    }
  }

  @media ${(props) => props.theme.device.mobileL} {
    padding: 0 0 0 5px;
    min-width: 80px;
    ${(props) => props.hasIcon && css``};
  }

  ${({ disabled, readonly }) =>
    disabled &&
    !readonly &&
    css`
      cursor: not-allowed;
      opacity: 0.6;
    `};

  ${({ readonly, theme }) =>
    readonly &&
    css`
      pointer-events: none;
      border: none;
      background-color: ${theme.colors.transparent};
      padding: 10px 0;
    `};

  &.site-input {
    background-color: ${(props) => props.theme.colors.bgPrimary};

    & input {
      color: ${(props) => props.theme.colors.textPrimary};
    }

    & ::placeholder {
      opacity: 1;
    }
  }

  &.modal-input {
    background: ${(props) => props.theme.colors.white};
    border: 1px solid ${(props) => props.theme.colors.black};
    border-radius: 4px;
    width: 280px;

    & input {
      color: ${(props) => props.theme.colors.textPrimary};
    }
  }

  .icon-x {
    position: absolute;
    right: 3px;
    top: 10px;
    stroke: ${(props) => props.theme.colors.textSecondary};
  }
`;

export const SpanIcon = styled.span`
  position: absolute;
  width: 50px;
  height: 40px;
  right: 0;
  top: -1px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0 6px 6px 0;
  background-color: ${(props) =>
    props.hasValue ? props.theme.colors.fail : 'transparent'};
  color: ${(props) =>
    props.hasValue ? props.theme.colors.white : props.theme.colors.black};
  cursor: pointer;
  font-size: 30px;
`;

export const ValidInput = styled.span`
  position: absolute;
  font-size: 35px;
  font-weight: 700;
  top: 50%;
  right: 5px;
  color: ${({ theme }) => theme.colors.green};
  transform: translate(0, -50%);
`;

export const Error = styled.div`
  color: ${(props) => props.theme.colors.fail};
  display: block;
  margin-top: 5px;
  width: max-content;
  max-width: 100%;
`;

export const Label = styled.label`
  color: ${(props) => props.theme.colors.textSecondary};
  position: absolute;
  top: 13px;

  & > div {
    margin-bottom: 5px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;

    @media ${(props) => props.theme.device.tabletL} {
      max-width: 300px;
    }

    @media ${(props) => props.theme.device.mobileM} {
      max-width: 130px;
    }

    @media ${(props) => props.theme.device.mobileS} {
      max-width: 100px;
    }
  }
`;

export const Icon = styled.div`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  font-size: 20px;
  color: ${(props) => props.theme.colors.emptyColor};
`;

export const InputWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => !['width', 'mb', 'size'].includes(prop),
})`
  width: ${({ width }) => (width ? `${width}px` : 'auto')};
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};
  ${({ size }) =>
    size === 'full' &&
    css`
      flex-grow: 1;
    `}

  ${({ size }) =>
    Number.isInteger(size)
      ? css`
          width: ${size}px;
          max-width: ${size}px;
        `
      : Object.keys(converter).includes(size)
      ? css`
          width: ${converter[size]};
          max-width: ${converter[size]};
        `
      : null}
`;
